import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Chip,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { TertiaryButton, SecondaryButton } from '../common/Buttons';
import { useNavigate } from 'react-router-dom';
import { getCalibrationDueEquipment } from '../../services/dashboard';
import { formatDateOnly, formatDateTime } from '../../utils/dateUtils';

const SEEN_KEY = 'calibrationAlertSeen';

/**
 * Popup shown once per login for equipment due or overdue for calibration.
 * Not stored/dismissible on the backend — the "seen" state lives in
 * sessionStorage (cleared on logout by AuthContext), so it survives page
 * refreshes within a session but shows again after the next login.
 */
function CalibrationDueAlert() {
    const [results, setResults] = useState([]);
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const navigate = useNavigate();

    const markSeen = () => {
        try {
            sessionStorage.setItem(SEEN_KEY, 'true');
        } catch (err) {
            // Storage unavailable (private browsing, etc.) — fine, just won't persist across refreshes.
        }
        setOpen(false);
    };

    useEffect(() => {
        let isMounted = true;
        const fetchData = async () => {
            let alreadySeen = false;
            try {
                alreadySeen = sessionStorage.getItem(SEEN_KEY) === 'true';
            } catch (err) {
                // Storage unavailable — treat as not-yet-seen.
            }
            try {
                const data = await getCalibrationDueEquipment();
                if (isMounted) {
                    const items = Array.isArray(data?.results) ? data.results : [];
                    setResults(items);
                    setCount(data?.count ?? 0);
                    if (items.length > 0 && !alreadySeen) setOpen(true);
                }
            } catch (err) {
                // Fail silently — this is a supplementary heads-up, not a page-blocking feature.
            }
        };
        fetchData();
        return () => { isMounted = false; };
    }, []);

    if (results.length === 0) {
        return null;
    }

    const hasOverdue = results.some((item) => item.is_overdue);

    return (
        <Dialog open={open} onClose={markSeen} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberIcon color={hasOverdue ? 'error' : 'warning'} />
                {hasOverdue ? 'Some equipment is overdue for calibration' : 'Equipment calibration coming up'}
            </DialogTitle>
            <DialogContent>
                {results.map((item) => (
                    <Box
                        key={item.id}
                        sx={{
                            cursor: 'pointer',
                            py: 1,
                            px: 1,
                            borderRadius: 1,
                            '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => {
                            markSeen();
                            navigate(`/inventory/equipment/${item.id}`);
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1">{item.name}</Typography>
                            <Typography variant="body2" color="text.secondary">({item.serial_no})</Typography>
                            <Chip
                                size="small"
                                color={item.is_overdue ? 'error' : 'warning'}
                                label={
                                    item.is_overdue
                                        ? `Overdue since ${formatDateOnly(item.next_calibration_date)}`
                                        : `Due ${formatDateOnly(item.next_calibration_date)}`
                                }
                            />
                        </Box>
                        {item.last_updated_by && (
                            <Typography variant="caption" color="text.secondary">
                                Last updated by {item.last_updated_by} on {formatDateTime(item.last_updated_at)}
                            </Typography>
                        )}
                    </Box>
                ))}
                {count > results.length && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Plus {count - results.length} more not shown here.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <TertiaryButton onClick={() => { markSeen(); navigate('/inventory?calibration_filter=due'); }}>
                    View in Inventory
                </TertiaryButton>
                <SecondaryButton onClick={markSeen}>
                    Close
                </SecondaryButton>
            </DialogActions>
        </Dialog>
    );
}

export default CalibrationDueAlert;
